import React, { useState, useEffect, useCallback } from 'react';
import { sucursalesAPI } from '../services/api';
import { toast } from 'react-toastify';

const Sucursales = () => {
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    codigo: '', nombre: '', direccion: '', telefono: '', email: '',
    ciudad: 'Quito', provincia: 'Pichincha',
    establecimiento_sri: '001', punto_emision_sri: '001',
    es_matriz: false, activo: true
  });

  const provincias = [
    'Azuay','Bolívar','Cañar','Carchi','Chimborazo','Cotopaxi','El Oro',
    'Esmeraldas','Galápagos','Guayas','Imbabura','Loja','Los Ríos',
    'Manabí','Morona Santiago','Napo','Orellana','Pastaza','Pichincha',
    'Santa Elena','Santo Domingo','Sucumbíos','Tungurahua','Zamora Chinchipe'
  ];

  const cargarSucursales = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sucursalesAPI.getAll();
      setSucursales(res.data.data || []);
    } catch {
      toast.error('Error al cargar sucursales');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarSucursales(); }, [cargarSucursales]);

  const resetForm = () => ({
    codigo: '', nombre: '', direccion: '', telefono: '', email: '',
    ciudad: 'Quito', provincia: 'Pichincha',
    establecimiento_sri: '001', punto_emision_sri: '001',
    es_matriz: false, activo: true
  });

  const openNew = () => {
    setEditing(null);
    setForm(resetForm());
    setShowModal(true);
  };

  const openEdit = (suc) => {
    setEditing(suc);
    setForm({
      codigo: suc.codigo || '',
      nombre: suc.nombre || '',
      direccion: suc.direccion || '',
      telefono: suc.telefono || '',
      email: suc.email || '',
      ciudad: suc.ciudad || 'Quito',
      provincia: suc.provincia || 'Pichincha',
      establecimiento_sri: suc.establecimiento_sri || '001',
      punto_emision_sri: suc.punto_emision_sri || '001',
      es_matriz: suc.es_matriz || false,
      activo: suc.activo !== false
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await sucursalesAPI.update(editing.id, form);
        toast.success('Sucursal actualizada');
      } else {
        await sucursalesAPI.create(form);
        toast.success('Sucursal creada');
      }
      setShowModal(false);
      cargarSucursales();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (suc) => {
    try {
      await sucursalesAPI.update(suc.id, { activo: !suc.activo });
      toast.success(suc.activo ? 'Sucursal desactivada' : 'Sucursal activada');
      cargarSucursales();
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1 fw-bold">
            <i className="bi bi-shop me-2 text-primary"></i>
            Gestión de Sucursales
          </h4>
          <p className="text-muted mb-0">Administra las sucursales y puntos de emisión SRI</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>
          <i className="bi bi-plus-lg me-1"></i> Nueva Sucursal
        </button>
      </div>

      {/* Cards de Sucursales */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
          <p className="text-muted mt-2">Cargando sucursales...</p>
        </div>
      ) : sucursales.length === 0 ? (
        <div className="text-center py-5">
          <i className="bi bi-shop display-1 text-muted"></i>
          <p className="text-muted mt-3">No hay sucursales registradas</p>
          <button className="btn btn-primary" onClick={openNew}>
            <i className="bi bi-plus-lg me-1"></i> Crear Primera Sucursal
          </button>
        </div>
      ) : (
        <div className="row g-3">
          {sucursales.map(suc => (
            <div key={suc.id} className="col-md-6 col-xl-4">
              <div className={`card h-100 border-0 shadow-sm ${!suc.activo ? 'opacity-50' : ''}`}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h5 className="card-title mb-1 fw-bold">
                        {suc.nombre}
                        {suc.es_matriz && (
                          <span className="badge bg-warning text-dark ms-2 small">MATRIZ</span>
                        )}
                      </h5>
                      <span className="badge bg-light text-dark">{suc.codigo}</span>
                    </div>
                    <span className={`badge ${suc.activo ? 'bg-success' : 'bg-secondary'}`}>
                      {suc.activo ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>

                  <div className="small text-muted">
                    {suc.direccion && (
                      <p className="mb-1">
                        <i className="bi bi-geo-alt me-1"></i> {suc.direccion}
                      </p>
                    )}
                    <p className="mb-1">
                      <i className="bi bi-pin-map me-1"></i> {suc.ciudad}, {suc.provincia}
                    </p>
                    {suc.telefono && (
                      <p className="mb-1">
                        <i className="bi bi-telephone me-1"></i> {suc.telefono}
                      </p>
                    )}
                    {suc.email && (
                      <p className="mb-1">
                        <i className="bi bi-envelope me-1"></i> {suc.email}
                      </p>
                    )}
                  </div>

                  <hr />

                  <div className="d-flex justify-content-between align-items-center">
                    <div className="small">
                      <span className="text-muted">SRI: </span>
                      <span className="fw-semibold">
                        Est. {suc.establecimiento_sri || '001'} - Pto. {suc.punto_emision_sri || '001'}
                      </span>
                    </div>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => openEdit(suc)}
                        title="Editar"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className={`btn ${suc.activo ? 'btn-outline-warning' : 'btn-outline-success'}`}
                        onClick={() => toggleActivo(suc)}
                        title={suc.activo ? 'Desactivar' : 'Activar'}
                      >
                        <i className={`bi ${suc.activo ? 'bi-pause-circle' : 'bi-play-circle'}`}></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Crear/Editar */}
      {showModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    <i className={`bi ${editing ? 'bi-pencil' : 'bi-plus-lg'} me-2`}></i>
                    {editing ? 'Editar Sucursal' : 'Nueva Sucursal'}
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)} />
                </div>
                <form onSubmit={handleSave}>
                  <div className="modal-body">
                    <div className="row g-3">
                      {/* Info básica */}
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold">Código *</label>
                        <input
                          type="text" className="form-control"
                          value={form.codigo}
                          onChange={e => setForm({...form, codigo: e.target.value.toUpperCase()})}
                          placeholder="SUC-001"
                          required
                        />
                      </div>
                      <div className="col-md-8">
                        <label className="form-label small fw-semibold">Nombre *</label>
                        <input
                          type="text" className="form-control"
                          value={form.nombre}
                          onChange={e => setForm({...form, nombre: e.target.value})}
                          placeholder="Sucursal Principal"
                          required autoFocus
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label small fw-semibold">Dirección</label>
                        <input
                          type="text" className="form-control"
                          value={form.direccion}
                          onChange={e => setForm({...form, direccion: e.target.value})}
                          placeholder="Av. Principal N12-34 y Calle Secundaria"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Ciudad</label>
                        <input
                          type="text" className="form-control"
                          value={form.ciudad}
                          onChange={e => setForm({...form, ciudad: e.target.value})}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Provincia</label>
                        <select
                          className="form-select"
                          value={form.provincia}
                          onChange={e => setForm({...form, provincia: e.target.value})}
                        >
                          {provincias.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Teléfono</label>
                        <input
                          type="text" className="form-control"
                          value={form.telefono}
                          onChange={e => setForm({...form, telefono: e.target.value})}
                          placeholder="02-2345678"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Email</label>
                        <input
                          type="email" className="form-control"
                          value={form.email}
                          onChange={e => setForm({...form, email: e.target.value})}
                          placeholder="sucursal@empresa.com"
                        />
                      </div>

                      {/* Datos SRI */}
                      <div className="col-12">
                        <hr className="my-1" />
                        <h6 className="fw-bold mt-2 mb-3">
                          <i className="bi bi-file-earmark-text me-1 text-info"></i>
                          Configuración SRI
                        </h6>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">
                          Código Establecimiento SRI
                        </label>
                        <input
                          type="text" className="form-control"
                          value={form.establecimiento_sri}
                          onChange={e => setForm({...form, establecimiento_sri: e.target.value})}
                          placeholder="001"
                          maxLength={3}
                        />
                        <div className="form-text">Código de 3 dígitos del establecimiento ante el SRI</div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">
                          Punto de Emisión SRI
                        </label>
                        <input
                          type="text" className="form-control"
                          value={form.punto_emision_sri}
                          onChange={e => setForm({...form, punto_emision_sri: e.target.value})}
                          placeholder="001"
                          maxLength={3}
                        />
                        <div className="form-text">Punto de emisión asignado por el SRI</div>
                      </div>

                      {/* Opciones */}
                      <div className="col-md-6">
                        <div className="form-check form-switch mt-3">
                          <input
                            type="checkbox" className="form-check-input"
                            id="esMatriz" checked={form.es_matriz}
                            onChange={e => setForm({...form, es_matriz: e.target.checked})}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="esMatriz">
                            <i className="bi bi-star-fill text-warning me-1"></i>
                            Es Sucursal Matriz
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch mt-3">
                          <input
                            type="checkbox" className="form-check-input"
                            id="activa" checked={form.activo}
                            onChange={e => setForm({...form, activo: e.target.checked})}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="activa">
                            Sucursal Activa
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? (
                        <><span className="spinner-border spinner-border-sm me-1"></span> Guardando...</>
                      ) : (
                        <><i className="bi bi-check-lg me-1"></i> {editing ? 'Actualizar' : 'Crear Sucursal'}</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Sucursales;
